import { useEffect, useState } from "react";
import { useAdminStore } from "../../lib/stores/admin-store";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { ErrorMessage } from "@/components/ui/ErrorMessage";
import { AdminDebug } from "@/components/admin/AdminDebug";
import { 
  Users, Calendar, Star, DollarSign, AlertCircle, 
  TrendingUp, User, UserX, PieChart, Download, ArrowUp, ArrowDown, Shield
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Link } from "react-router-dom";

// Extended interfaces for the properties we use that aren't in the base types
interface ExtendedExperience {
  id: string;
  title?: string;
  name?: string;
  description?: string;
  creator_id?: string;
  creator_name?: string;
  creator?: {
    id: string;
    full_name?: string;
    creator_profile?: any;
    creator_profiles?: any[];
  };
  status?: string;
  price?: number;
  created_at: string;
}

interface ExtendedBooking {
  id: string;
  experience_id?: string;
  user_id: string;
  experience?: {
    id: string;
    title?: string;
    creator?: {
      id: string;
      full_name?: string;
      creator_profile?: any;
      creator_profiles?: any[];
    };
  };
  experience_name?: string;
  amount?: number;
  total_amount?: number;
  status?: string;
  created_at: string;
}

/**
 * Admin Dashboard Overview Component
 * Enhanced with better error handling and partial data loading
 */
export function AdminOverviewPage() {
  const { 
    isInitialized, 
    error, 
    stats, 
    users, 
    bookings, 
    experiences, 
    pendingExperiences,
    fetchStats,
    fetchUsers,
    fetchBookings,
    fetchExperiences,
    fetchPendingExperiences
  } = useAdminStore();
  
  const [activeTab, setActiveTab] = useState<'summary' | 'creators' | 'users'>('summary');

  // Refresh data when component mounts
  useEffect(() => {
    if (isInitialized) {
      // Attempt to reload each data type individually
      // If one fails, the others can still succeed
      fetchStats().catch(err => 
        console.error("[AdminOverview] Error refreshing stats:", err)
      );
      
      fetchUsers().catch(err => 
        console.error("[AdminOverview] Error refreshing users:", err)
      );
      
      fetchBookings().catch(err => 
        console.error("[AdminOverview] Error refreshing bookings:", err)
      );
      
      fetchExperiences().catch(err => 
        console.error("[AdminOverview] Error refreshing experiences:", err)
      );
      
      fetchPendingExperiences().catch(err => 
        console.error("[AdminOverview] Error refreshing pending experiences:", err)
      );
    }
  }, [isInitialized, fetchStats, fetchUsers, fetchBookings, fetchExperiences, fetchPendingExperiences]);

  // Full page loading state
  if (!isInitialized) {
    return <LoadingSpinner />;
  }

  // Cast to our extended interfaces
  const typedExperiences = experiences as ExtendedExperience[];
  const typedBookings = bookings as ExtendedBooking[];
  const typedPendingExperiences = pendingExperiences as ExtendedExperience[];

  // Calculate summarized data
  const creators = users.filter(user => user.role === 'creator');
  const regularUsers = users.filter(user => user.role === 'user');
  const admins = users.filter(user => user.role === 'admin');
  
  // Recent users (last 30 days)
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  
  const recentUsers = users.filter(user => 
    new Date(user.created_at) > thirtyDaysAgo
  ).length;
  
  const recentCreators = creators.filter(user => 
    new Date(user.created_at) > thirtyDaysAgo
  ).length;

  // Calculate creators with experiences
  const creatorsWithExperiences = new Set();
  typedExperiences.forEach(exp => {
    if (exp.creator_id) creatorsWithExperiences.add(exp.creator_id);
  });

  const statCards = [
    {
      label: "Total Users",
      value: stats.totalUsers || 0,
      icon: Users,
      color: "bg-blue-500/20",
      textColor: "text-blue-400",
      change: recentUsers,
      link: "/admin/users",
    },
    {
      label: "Total Bookings",
      value: stats.totalBookings || 0, 
      icon: Calendar,
      color: "bg-green-500/20",
      textColor: "text-green-400",
      link: "/admin/bookings",
    },
    {
      label: "Total Experiences",
      value: stats.totalExperiences || 0,
      icon: Star,
      color: "bg-yellow-500/20",
      textColor: "text-yellow-400",
      link: "/admin/experiences",
    },
    {
      label: "Total Revenue",
      value: `$${(stats.revenue || 0).toLocaleString()}`,
      icon: DollarSign,
      color: "bg-purple-500/20",
      textColor: "text-purple-400",
      link: "/admin/payments",
    },
  ];

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Debug Information */}
      <AdminDebug />
      
      {/* Dashboard Header with Welcome Message */}
      <div className="mb-8">
        <h1 className="text-2xl font-display text-sand-100 mb-2">Rendezvous Admin Dashboard</h1>
        <p className="text-sand-400">Manage your marketplace experiences, users, and transactions</p>
      </div>

      {/* Errors banner - show errors but don't block the UI */}
      {error && (
        <div className="bg-red-900/30 border-l-4 border-red-500 p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="h-6 w-6 text-red-400 mr-3" />
            <div>
              <p className="text-sm text-red-300 font-medium">
                Some data could not be loaded
              </p>
              <p className="text-sm text-red-400">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => (
          <Link key={stat.label} to={stat.link} className="block">
            <div className="bg-earth-700 overflow-hidden shadow rounded-lg transition-transform hover:scale-[1.02] cursor-pointer border border-earth-600">
              <div className="p-5">
                <div className="flex items-center">
                  <div className={`flex-shrink-0 rounded-full p-3 ${stat.color}`}>
                    <stat.icon className={`h-6 w-6 ${stat.textColor}`} />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-sand-400 truncate">{stat.label}</dt>
                      <dd className="text-lg font-medium text-sand-100">{stat.value}</dd>
                      {stat.change !== undefined && (
                        <dd className="text-xs font-medium text-sand-400">
                          +{stat.change} in last 30 days
                        </dd>
                      )}
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Tabs for different view modes */}
      <div className="flex flex-wrap space-x-2 border-b border-earth-600 pb-2 mt-8">
        <button
          onClick={() => setActiveTab('summary')}
          className={`px-4 py-2 text-sm font-medium rounded-t-md ${
            activeTab === 'summary' 
              ? 'bg-earth-600 text-sand-100' 
              : 'text-sand-400 hover:text-sand-300'
          }`}
        >
          Summary
        </button>
        <button
          onClick={() => setActiveTab('creators')}
          className={`px-4 py-2 text-sm font-medium rounded-t-md ${
            activeTab === 'creators' 
              ? 'bg-earth-600 text-sand-100' 
              : 'text-sand-400 hover:text-sand-300'
          }`}
        >
          Creators
        </button>
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 text-sm font-medium rounded-t-md ${
            activeTab === 'users' 
              ? 'bg-earth-600 text-sand-100' 
              : 'text-sand-400 hover:text-sand-300'
          }`}
        >
          Users
        </button>
      </div>

      {/* Summary Tab */}
      {activeTab === 'summary' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* User Stats Summary */}
          <div className="bg-earth-700 shadow rounded-lg p-6">
            <h3 className="text-lg font-display text-sand-100 mb-4">User Breakdown</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <User className="h-5 w-5 text-blue-400 mr-2" />
                  <span className="text-sand-300">Regular Users</span>
                </div>
                <div className="flex items-center">
                  <span className="text-sand-100 font-medium">{regularUsers.length}</span>
                  <span className="text-sand-400 text-xs ml-2">({((regularUsers.length / users.length) * 100).toFixed(1)}%)</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <Star className="h-5 w-5 text-yellow-400 mr-2" />
                  <span className="text-sand-300">Creators</span>
                </div>
                <div className="flex items-center">
                  <span className="text-sand-100 font-medium">{creators.length}</span>
                  <span className="text-sand-400 text-xs ml-2">({((creators.length / users.length) * 100).toFixed(1)}%)</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <Shield className="h-5 w-5 text-purple-400 mr-2" />
                  <span className="text-sand-300">Admins</span>
                </div>
                <div className="flex items-center">
                  <span className="text-sand-100 font-medium">{admins.length}</span>
                  <span className="text-sand-400 text-xs ml-2">({((admins.length / users.length) * 100).toFixed(1)}%)</span>
                </div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-earth-600">
              <div className="flex items-center justify-between">
                <div className="text-sand-400 text-sm">New users in last 30 days</div>
                <div className="text-sand-100">{recentUsers}</div>
              </div>
            </div>
            <div className="mt-6">
              <Link to="/admin/users">
                <Button variant="outline" className="w-full justify-center bg-earth-600 hover:bg-earth-500 text-sand-200">
                  View All Users
                </Button>
              </Link>
            </div>
          </div>

          {/* Experience Stats */}
          <div className="bg-earth-700 shadow rounded-lg p-6">
            <h3 className="text-lg font-display text-sand-100 mb-4">Experience Overview</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <Star className="h-5 w-5 text-yellow-400 mr-2" />
                  <span className="text-sand-300">Total Experiences</span>
                </div>
                <div>
                  <span className="text-sand-100 font-medium">{typedExperiences.length}</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <UserX className="h-5 w-5 text-orange-400 mr-2" />
                  <span className="text-sand-300">Pending Approval</span>
                </div>
                <div>
                  <span className="text-sand-100 font-medium">{typedPendingExperiences.length}</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <Users className="h-5 w-5 text-green-400 mr-2" />
                  <span className="text-sand-300">Creators with Experiences</span>
                </div>
                <div>
                  <span className="text-sand-100 font-medium">{creatorsWithExperiences.size}</span>
                  <span className="text-sand-400 text-xs ml-2">of {creators.length}</span>
                </div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-earth-600">
              <div className="flex items-center justify-between">
                <div className="text-sand-400 text-sm">Avg. Experiences per Creator</div>
                <div className="text-sand-100">
                  {creators.length ? (typedExperiences.length / creators.length).toFixed(1) : 0}
                </div>
              </div>
            </div>
            <div className="mt-6">
              <Link to="/admin/experiences">
                <Button variant="outline" className="w-full justify-center bg-earth-600 hover:bg-earth-500 text-sand-200">
                  Manage Experiences
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Creator Tab */}
      {activeTab === 'creators' && (
        <div className="bg-earth-700 shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
            <h3 className="text-lg leading-6 font-display text-sand-100">Creator Overview</h3>
            <Button variant="outline" size="sm" className="bg-earth-600 text-sand-300">
              <Download className="h-4 w-4 mr-1" /> Export
            </Button>
          </div>
          <div className="border-t border-earth-600">
            <div className="px-4 py-5 sm:p-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-earth-600">
                  <thead>
                    <tr>
                      <th className="px-3 py-3.5 text-left text-xs font-medium text-sand-400 uppercase tracking-wider">Creator</th>
                      <th className="px-3 py-3.5 text-left text-xs font-medium text-sand-400 uppercase tracking-wider">Experiences</th>
                      <th className="px-3 py-3.5 text-left text-xs font-medium text-sand-400 uppercase tracking-wider">Bookings</th>
                      <th className="px-3 py-3.5 text-left text-xs font-medium text-sand-400 uppercase tracking-wider">Revenue</th>
                      <th className="px-3 py-3.5 text-left text-xs font-medium text-sand-400 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-earth-600">
                    {creators.slice(0, 5).map((creator) => {
                      const creatorExps = typedExperiences.filter(exp => exp.creator_id === creator.id);
                      const creatorBookingCount = typedBookings.filter(b => 
                        creatorExps.some(exp => exp.id === b.experience_id)
                      ).length;
                      
                      return (
                        <tr key={creator.id} className="hover:bg-earth-600/50">
                          <td className="px-3 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="text-sm font-medium text-sand-200">{creator.email}</div>
                            </div>
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap">
                            <div className="text-sm text-sand-300">{creatorExps.length}</div>
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap">
                            <div className="text-sm text-sand-300">{creatorBookingCount}</div>
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap">
                            <div className="text-sm text-sand-300">
                              ${typedBookings
                                .filter(b => creatorExps.some(exp => exp.id === b.experience_id))
                                .reduce((sum, b) => sum + (b.total_amount || 0), 0)
                                .toFixed(2)
                              }
                            </div>
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap">
                            <div className="text-sm text-sand-300">
                              {creatorExps.length > 0 ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-900/30 text-green-300">
                                  Active
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-900/30 text-yellow-300">
                                  No Experiences
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {creators.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-6 py-4 text-center text-sand-400">
                          No creators found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {creators.length > 5 && (
                  <div className="text-center pt-4">
                    <Link to="/admin/users?role=creator">
                      <Button variant="outline" size="sm" className="bg-earth-600 text-sand-300">
                        View All Creators
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="bg-earth-700 shadow rounded-lg">
          <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
            <h3 className="text-lg leading-6 font-display text-sand-100">User Growth</h3>
            <div className="flex space-x-2">
              <span className="text-sand-400 text-sm">Last 30 days: </span>
              <span className="text-green-400 text-sm flex items-center">
                <ArrowUp className="h-3 w-3 mr-1" />
                {recentUsers}
              </span>
            </div>
          </div>
          <div className="border-t border-earth-600">
            <div className="px-4 py-5 sm:p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-earth-600/30 p-4 rounded-lg">
                  <div className="text-2xl sm:text-4xl font-display text-sand-100">{users.length}</div>
                  <div className="text-sm text-sand-400">Total users</div>
                </div>
                <div className="bg-earth-600/30 p-4 rounded-lg">
                  <div className="text-2xl sm:text-4xl font-display text-sand-100">{recentUsers}</div>
                  <div className="text-sm text-sand-400">New (30 days)</div>
                </div>
                <div className="bg-earth-600/30 p-4 rounded-lg">
                  <div className="text-2xl sm:text-4xl font-display text-sand-100">{regularUsers.length}</div>
                  <div className="text-sm text-sand-400">Regular users</div>
                </div>
                <div className="bg-earth-600/30 p-4 rounded-lg">
                  <div className="text-2xl sm:text-4xl font-display text-sand-100">{creators.length}</div>
                  <div className="text-sm text-sand-400">Creators</div>
                </div>
              </div>
              
              {/* Mock Chart - In production, you would integrate a real chart here */}
              <div className="h-40 bg-earth-600/30 rounded-lg flex items-center justify-center mb-6">
                <PieChart className="h-8 w-8 text-sand-400 mr-3 opacity-70" />
                <span className="text-sand-400">User Growth Chart Placeholder</span>
              </div>
              
              <div className="text-center">
                <Link to="/admin/users">
                  <Button variant="outline" className="bg-earth-600 hover:bg-earth-500 text-sand-200">
                    View User Details
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Activity */}
      <div className="bg-earth-700 shadow rounded-lg">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
          <h3 className="text-lg leading-6 font-display text-sand-100">Recent Activity</h3>
          <Link to="/admin/activity" className="text-sm text-sand-400 hover:text-sand-300">
            View all
          </Link>
        </div>
        <div className="border-t border-earth-600">
          <div className="px-4 py-5 sm:p-6">
            {typedBookings.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="h-12 w-12 text-sand-500/20 mx-auto mb-3" />
                <p className="text-sm text-sand-400">No recent bookings found.</p>
                <p className="text-xs text-sand-500 mt-1">New bookings will appear here when created.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {typedBookings.slice(0, 5).map((booking) => (
                  <div key={booking.id} className="flex items-center p-3 space-x-4 rounded-lg hover:bg-earth-600/50 transition-colors">
                    <div className="flex-shrink-0">
                      <div className="bg-earth-600 p-2 rounded-full">
                        <Calendar className="h-5 w-5 text-sand-300" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-sand-200 truncate">
                        New booking for {booking.experience_name || booking.experience?.title || "Unknown Experience"}
                      </p>
                      <p className="text-xs text-sand-400 mt-1 flex items-center">
                        <span className="mr-2">{new Date(booking.created_at).toLocaleDateString()}</span>
                        {booking.status && (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            booking.status === 'confirmed' ? 'bg-green-900/30 text-green-300' :
                            booking.status === 'cancelled' ? 'bg-red-900/30 text-red-300' :
                            'bg-yellow-900/30 text-yellow-300'
                          }`}>
                            {booking.status}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-900/30 text-green-300">
                        ${booking.total_amount || booking.amount || 0}
                      </span>
                    </div>
                  </div>
                ))}
                {typedBookings.length > 5 && (
                  <div className="pt-3 text-center">
                    <Link to="/admin/bookings" className="text-sm text-sand-400 hover:text-sand-300 inline-flex items-center">
                      <span>View all bookings</span>
                      <span className="ml-1">→</span>
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 